import { hashKey, useQuery } from '@tanstack/react-query';

import { BatchListItem } from '@e-origin/contracts';
import { BatchTypeEnum, IBatchesCriteria, SortDirection, TemplateType } from '@e-origin/shared';

import { queryClient } from '@hooks/queryClient';
import { showErrorToast } from '@hooks/showErrorToast';
import { apiClient, STORAGE_KEYS } from '@utils';
import { getStoredCriteria } from '../../../criteria.caching';

const defaultBatchesCriteria: IBatchesCriteria = {
  filters: { filterByUsers: [], filterByNotifications: [] },
  pagination: { page: 1, size: 10, direction: 1, searchToken: '', currentToken: '' },
  sorting: { field: 'counter', direction: SortDirection.DESC },
};

export const getInitialCriteria = () => {
  const storedCriteria = getStoredCriteria(STORAGE_KEYS.COOKIES.LIST_BATCHES_CRITERIA);
  const criteria = storedCriteria ?? defaultBatchesCriteria;

  const batchView = getStoredCriteria(STORAGE_KEYS.COOKIES.BATCH_VIEW) as BatchTypeEnum;
  if (!batchView || [BatchTypeEnum.HIGH_VALUES, BatchTypeEnum.HIGH_VALUES_EXPORT].includes(batchView)) {
    criteria.filters.templateType = TemplateType.HIGH_VALUE_H1;
  } else {
    criteria.filters.templateType = TemplateType.LOW_VALUE_H7;
  }

  return criteria;
};

const fetchBatches = async (criteria: IBatchesCriteria): Promise<{ list: BatchListItem[]; total: number }> => {
  try {
    const result = await apiClient.batchesCore.listBatches.mutation({ body: { criteria } });
    if (result.status !== 200) throw result.body;

    const { list, totalItems } = result.body.data;
    return {
      list: list.map((b) => ({
        ...b,
        createdAt: b.createdAt ? new Date(b.createdAt) : undefined,
        updatedAt: b.updatedAt ? new Date(b.updatedAt) : undefined,
      })),
      total: totalItems,
    };
  } catch (error) {
    showErrorToast(error, 'Failed to fetch batches');
  }
};

export const useBatches = (criteria: IBatchesCriteria) =>
  useQuery({
    queryKey: ['batches', hashKey([criteria])],
    queryFn: () => fetchBatches(criteria),
  });

export const useBatchesCustomsReport = (criteria: IBatchesCriteria) =>
  useQuery({
    queryKey: ['batches-customs-report', hashKey([criteria])],
    queryFn: () => fetchBatches(criteria),
  });

export const prefetchBatches = (criteria: IBatchesCriteria) =>
  queryClient.prefetchQuery({
    queryKey: ['batches', hashKey([criteria])],
    queryFn: () => fetchBatches(criteria),
  });
